import {
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { defineMessage } from 'react-intl';
import {
  AttributeUpdates,
  ChallengeRequired,
  cancelAuthByUserId,
  UserAttributes,
  finishAttributeUpdate,
  finishAuth,
  finishLogout,
  requireChallenge,
  selectAuthentication,
  setErrorMessage,
  startAttributeUpdate,
  startAuthByEmail,
  startAuthByUserId,
  startChallenge,
  startLogout,
  startResendChallengeCode,
} from './authSlice';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import config from '@utils/config';
import { makeGraphQlCall } from '@utils/graphql';
import { selectEndpoint } from '../../../endpoint/endpointSlice';
import { USER_ID_AUTH_TIMEOUT_MS, isIdentifierOfCustomer } from './userIdAuth';
import { log } from '@utils/logger';

const userPool = new CognitoUserPool(config.cognitoConfig);

// How long a code submit or a Resend may run before the Pro can try again
export const CODE_STEP_TIMEOUT_MS = 20000;

const lookupIdentifierQuery = `query LookupIdentifier($identifier: ID!) {
  lookupIdentifier(identifier: $identifier) {
    customerProfessionalId
    customer {
      customerId
    }
    professional {
      primaryEmailAddress
      professionalId
    }
  }
}`;

const createIdentifierMutation = `mutation CreateIdentifier($customerId: ID!, $emailAddress: String!) {
  createIdentifier(customerId: $customerId, emailAddress: $emailAddress) {
    customerProfessionalId
  }
}`;

defineMessage({
  id: 'authentication.error.genericError',
  description: 'Generic error from the authentication flow',
  defaultMessage:
    'There was an error during authentication. Please try again, or contact support@mesh.id for assistance.',
});

defineMessage({
  id: 'authentication.error.notLoggedIn',
  description: 'Error with not being correctly logged in',
  defaultMessage:
    'There was an error retrieving the logged in user. Please try again, or contact support@mesh.id for assistance.',
});
defineMessage({
  id: 'authentication.error.invalidEmail',
  description: 'Invalid email error from the authentication flow',
  defaultMessage: 'Invalid email.',
});
export const invalidCode = defineMessage({
  id: 'authentication.error.invalidCode',
  description: 'Invalid code error from the authentication flow',
  defaultMessage: 'Invalid code.',
});
defineMessage({
  id: 'authentication.error.logoutError',
  description: 'Logout error from the authentication flow',
  defaultMessage: 'Error during logout.',
});

let user: CognitoUser;
const createUser = (username: string) => {
  const userData = {
    Username: username,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');
  user = cognitoUser;
  return user;
};
const getUser = async () => {
  if (user != null) {
    return user;
  }
  // If not in local variable, try to fetch from the userPool
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser != null) {
    return cognitoUser;
  }
  throw new Error('No user was found!');
};

const createIdentifier = async ({
  emailAddress,
  customerId,
}: {
  emailAddress: string;
  customerId: string;
}) => {
  const data = await makeGraphQlCall({
    query: createIdentifierMutation,
    variables: {
      customerId,
      emailAddress,
    },
  });
  console.log('got response', data);
  return data.createIdentifier.customerProfessionalId;
};

const lookupIdentifier = async ({ identifier }: { identifier: string }) => {
  const data = await makeGraphQlCall({
    query: lookupIdentifierQuery,
    variables: {
      identifier,
    },
  });
  console.log('got response', data);
  return {
    emailAddress: data.lookupIdentifier?.professional?.primaryEmailAddress,
    customerId: data.lookupIdentifier?.customer?.customerId,
  };
};

const performLogin = async ({
  username,
  customerProfessionalId,
  cognitoUser,
}: {
  username: string;
  cognitoUser: CognitoUser;
  customerProfessionalId: string;
}) => {
  const authenticationData = {
    Username: username,
    AuthParameters: {
      CHALLENGE_NAME: 'CUSTOM_CHALLENGE',
      USERNAME: username,
    },
  };
  const authenticationDetails = new AuthenticationDetails(authenticationData);
  return new Promise((resolve, reject) => {
    cognitoUser.initiateAuth(authenticationDetails, {
      onSuccess: async result => {
        console.log('success on auth', result);
        const accessToken = result.getAccessToken().getJwtToken();
        console.log('got accessToken', accessToken);
        try {
          const { emailAddress, phoneNumber, firstName, lastName, birthdate } =
            await getUserAttributes({
              cognitoUser,
            });

          resolve(
            finishAuth({
              emailAddress,
              phoneNumber,
              firstName,
              lastName,
              birthdate,
              accessToken,
              customerProfessionalId,
            })
          );
        } catch (e) {
          reject(e);
        }
      },

      customChallenge: challengeParameters => {
        console.log('got challenge', challengeParameters);
        // User authentication depends on challenge response
        resolve(
          requireChallenge({ emailAddress: username, customerProfessionalId })
        );
      },
      onFailure: err => {
        console.log('error on auth', err);
        // alert(err.message || JSON.stringify(err));
        reject(err);
      },
    });
  });
};

const setAttributes = async ({
  cognitoUser,
  attributes,
}: {
  cognitoUser: CognitoUser;
  attributes: Record<string, string>;
}): Promise<void> => {
  const cognitoAttributes = Object.entries(attributes).map(([Name, Value]) => {
    return { Name, Value } as CognitoUserAttribute;
  });
  return new Promise((fulfill, reject) => {
    cognitoUser.updateAttributes(cognitoAttributes, (err, result, details) => {
      if (err) {
        console.log('got err', err);
        reject(err);
        return;
      }
      console.log('got result', result);
      fulfill();
    });
  });
};

const tryChallengeCode = async ({
  challengeCode,
  cognitoUser,
  customerProfessionalId,
}: {
  challengeCode: string;
  cognitoUser: CognitoUser;
  customerProfessionalId: string;
}) => {
  return new Promise((resolve, reject) => {
    cognitoUser.sendCustomChallengeAnswer(challengeCode, {
      onSuccess: async result => {
        console.log('success on auth', result);
        const accessToken = result.getAccessToken().getJwtToken();
        console.log('got accessToken', accessToken);

        try {
          const { emailAddress, phoneNumber, firstName, lastName, birthdate } =
            await getUserAttributes({
              cognitoUser,
            });

          resolve(
            finishAuth({
              emailAddress,
              phoneNumber,
              firstName,
              lastName,
              birthdate,
              accessToken,
              customerProfessionalId,
            })
          );
        } catch (e) {
          reject(e);
        }
      },
      customChallenge: challengeParameters => {
        console.log('got challenge', challengeParameters);
        // User authentication depends on challenge response
        reject(new Error('Invalid code. Please try again'));
      },
      onFailure: err => {
        console.log('error on auth', err);
        reject(err);
      },
    });
  });
};

const performLogout = async ({ cognitoUser }: { cognitoUser: CognitoUser }) => {
  return new Promise((resolve, reject) => {
    try {
      cognitoUser.signOut(() => {
        resolve('Done');
      });
    } catch (e) {
      console.log('Error while signing out', e);
      reject(e);
    }
  });
};

export const triggerPhoneNumberVerification = async (): Promise<void> => {
  const cognitoUser: CognitoUser = await getUser();
  if (!cognitoUser) {
    throw new Error('No user was found!');
  }
  return new Promise((fulfill, reject) => {
    console.log('triggering phone verification');
    cognitoUser.getAttributeVerificationCode('phone_number', {
      onSuccess: (success: string) => {
        console.log('success', success);
        fulfill();
      },
      onFailure: (err: Error) => {
        console.log('got err', err);
        reject(err);
      },
    });

    // cognitoUser.verifyAttribute()
  });
};

export const verifyPhoneNumber = async ({
  confirmationCode,
}: {
  confirmationCode: string;
}): Promise<void> => {
  const cognitoUser: CognitoUser = await getUser();
  if (!cognitoUser) {
    throw new Error('No user was found!');
  }
  return new Promise((fulfill, reject) => {
    console.log('verifying phone_number with code');
    cognitoUser.verifyAttribute('phone_number', confirmationCode, {
      onSuccess: (success: string) => {
        console.log('success', success);
        fulfill();
      },
      onFailure: (err: Error) => {
        console.log('got err', err);
        reject(err);
      },
    });
  });
};

export const setUserAttributes = async (attributeUpdates: AttributeUpdates) => {
  const cognitoUser: CognitoUser = await getUser();
  if (!cognitoUser) {
    throw new Error('No user was found!');
  }
  const attributes: Record<string, string> = {};
  if (attributeUpdates.phoneNumber) {
    attributes['phone_number'] = attributeUpdates.phoneNumber;
  }
  if (attributeUpdates.firstName) {
    attributes['given_name'] = attributeUpdates.firstName;
  }
  if (attributeUpdates.lastName) {
    attributes['family_name'] = attributeUpdates.lastName;
  }
  if (attributeUpdates.birthdate) {
    attributes['birthdate'] = attributeUpdates.birthdate;
  }
  await setAttributes({ cognitoUser, attributes });
};

const getUserAttributes = async ({
  cognitoUser,
}: {
  cognitoUser: CognitoUser;
}): Promise<UserAttributes> => {
  return new Promise((resolve, reject) => {
    try {
      cognitoUser.getUserAttributes((e, attrs) => {
        if (e) {
          console.log('Error getting attributes');
          reject(e);
          return;
        }
        const mapped = Object.fromEntries(
          attrs.map(({ Name, Value }) => [Name, Value])
        ) as {
          email: string;
          phone_number?: string;
          phone_number_verified?: string;
          given_name?: string;
          family_name?: string;
          birthdate?: string;
        };

        const emailAddress: string = mapped['email'];
        // Only output the phoneNumber if it's been verified
        const phoneNumber: string =
          mapped['phone_number_verified'] === 'true'
            ? mapped['phone_number']
            : null;
        const firstName: string = mapped['given_name'];
        const lastName: string = mapped['family_name'];
        const birthdate: string = mapped['birthdate'];

        resolve({
          emailAddress,
          phoneNumber,
          firstName,
          lastName,
          birthdate,
        });
      });
    } catch (e) {
      console.log('Error getting attributes');
      throw e;
    }
  });
};

function* handleStartAuthByEmail(action: PayloadAction<string>) {
  try {
    log.debug('got action', action);
    const endpointConfig = selectEndpoint(yield select());
    log.debug('got endpointConfig', endpointConfig);

    const identifier: string = yield call(createIdentifier, {
      emailAddress: action.payload,
      customerId: endpointConfig.customer.customerId,
    });

    const cognitoUser = createUser(action.payload);
    const outputAction: PayloadAction = yield call(performLogin, {
      username: action.payload,
      cognitoUser,
      customerProfessionalId: identifier,
    });
    log.debug('got action', { outputAction, identifier });
    yield put(outputAction);
  } catch (e) {
    log.debug('got error', e);
    yield put(setErrorMessage('authentication.error.genericError'));
  }
}

function* authByUserId(identifier: string) {
  const endpointConfig = selectEndpoint(yield select());
  // First, we call "lookupIdentifier" to obtain the email address
  const {
    emailAddress,
    customerId,
  }: { emailAddress?: string; customerId?: string } = yield call(
    lookupIdentifier,
    {
      identifier,
    }
  );
  console.log('got email', emailAddress);
  // Only log in with an identifier of this endpoint's customer. Otherwise the
  // Pro enters their email, which creates this customer's identifier.
  if (!isIdentifierOfCustomer(customerId, endpointConfig.customer.customerId)) {
    console.log('identifier is not for this customer, asking for email');
    yield put(cancelAuthByUserId());
    return;
  }
  const cognitoUser = createUser(emailAddress);
  const outputAction: PayloadAction = yield call(performLogin, {
    username: emailAddress,
    cognitoUser,
    customerProfessionalId: identifier,
  });
  console.log('got action', outputAction, emailAddress);
  yield put(outputAction);
}

function* handleStartAuthByUserId(action: PayloadAction<string>) {
  try {
    console.log('got action', action);
    // The email step waits while this runs, so give up after a while and let
    // the Pro enter their email
    const { timedOut } = yield race({
      done: call(authByUserId, action.payload),
      timedOut: delay(USER_ID_AUTH_TIMEOUT_MS),
    });
    if (timedOut) {
      log.warn('id login timed out, asking for email', {
        timeoutMs: USER_ID_AUTH_TIMEOUT_MS,
      });
      yield put(cancelAuthByUserId());
    }
  } catch (e) {
    console.log('got error', e);
    yield put(setErrorMessage('authentication.error.invalidEmail'));
  }
}

function* handleStartChallenge(action: PayloadAction<string>) {
  try {
    console.log('got action', action);
    const cognitoUser: CognitoUser = yield call(getUser);

    const { customerProfessionalId } = selectAuthentication(yield select());
    const outputAction: PayloadAction = yield call(tryChallengeCode, {
      challengeCode: action.payload,
      cognitoUser,
      customerProfessionalId,
    });
    console.log('got action', outputAction);
    yield put(outputAction);
  } catch (e) {
    console.log('got error', e);
    yield put(setErrorMessage('authentication.error.invalidCode'));
  }
}
function* handleStartLogout() {
  console.log('Starting logout');
  try {
    const cognitoUser: CognitoUser = yield call(getUser);
    if (cognitoUser) {
      yield call(performLogout, { cognitoUser });
    }
    yield put(finishLogout());
  } catch (e) {
    console.log('got error', e);
    yield put(setErrorMessage('authentication.error.logoutError'));
  }
}

// Starts the login again, which sends a new code. It keeps the identifier the
// code step was reached with instead of resolving it again from the email.
function* handleResendChallengeCode() {
  console.log('Resending challenge code');
  try {
    const { emailAddress, customerProfessionalId } = selectAuthentication(
      yield select()
    );
    if (!emailAddress || !customerProfessionalId) {
      throw new Error('No login to resend a code for');
    }
    const cognitoUser = createUser(emailAddress);
    const outputAction: PayloadAction = yield call(performLogin, {
      username: emailAddress,
      cognitoUser,
      customerProfessionalId,
    });
    yield put(outputAction);
  } catch (e) {
    console.log('got error', e);
    yield put(setErrorMessage('authentication.error.genericError'));
  }
}

// One code-step request at a time: a code submitted or a Resend clicked while
// another is running is ignored. A request that never answers gives the slot
// back after a while, so the Pro can try again, and one is dropped when the Pro
// leaves the code step for another login.
function* handleCodeStepAction(action: PayloadAction<string>) {
  const { timedOut } = yield race({
    done:
      action.type === startChallenge.type
        ? call(handleStartChallenge, action)
        : call(handleResendChallengeCode),
    timedOut: delay(CODE_STEP_TIMEOUT_MS),
    leftCodeStep: take([
      startLogout.type,
      startAuthByEmail.type,
      startAuthByUserId.type,
    ]),
  });
  if (timedOut) {
    log.warn('code step request timed out', {
      timeoutMs: CODE_STEP_TIMEOUT_MS,
    });
    yield put(setErrorMessage('authentication.error.genericError'));
  }
}

function* handleStartAttributeUpdate(action: PayloadAction<AttributeUpdates>) {
  try {
    yield call(setUserAttributes, action.payload);
    const cognitoUser: CognitoUser = yield call(getUser);
    if (!cognitoUser) {
      yield put(setErrorMessage('authentication.error.genericError'));
    }
    const attributes: Record<string, string> = {};
    if (action.payload.phoneNumber) {
      attributes['phone_number'] = action.payload.phoneNumber;
    }
    if (action.payload.firstName) {
      attributes['given_name'] = action.payload.firstName;
    }
    if (action.payload.lastName) {
      attributes['family_name'] = action.payload.lastName;
    }
    if (action.payload.birthdate) {
      attributes['birthdate'] = action.payload.birthdate;
    }
    yield call(setAttributes, { cognitoUser, attributes });
    yield put(finishAttributeUpdate(action.payload));
  } catch (e) {
    console.log('got error', e);
    yield put(setErrorMessage('authentication.error.genericError'));
  }
}

function* authSaga() {
  yield takeEvery(startLogout.type, handleStartLogout);
  yield takeEvery(startAuthByEmail.type, handleStartAuthByEmail);
  yield takeEvery(startAuthByUserId.type, handleStartAuthByUserId);
  yield takeLeading(
    [startChallenge.type, startResendChallengeCode.type],
    handleCodeStepAction
  );
  yield takeEvery(startAttributeUpdate.type, handleStartAttributeUpdate);
}

export default authSaga;
